// PnLStatementBody — iter180p
//
// Read-only renderer used by Review & Compare. Mirrors the MTD by Outlet
// statement EXACTLY: same sections, same row labels, same hierarchy, same
// values. ALL rows come from the backend's `block._statement` object
// (op_ex_items + fixed_items) — there are NO hardcoded row labels here.
//
// Column layout (2-period compare): LINE ITEM · A · B · VARIANCE · VAR % · COST %
//
// The data parity contract is enforced server-side by
// `services/pnl_statement_rows.build_statement_rows` and verified by
// `tests/test_pnl_review_parity_iter180o.py` (Review budget block ↔ MTD UI
// budget for every outlet × Apr→Mar FY · 0 mismatches).

const fmt = (v) => {
  if (v === null || v === undefined || (typeof v === 'number' && isNaN(v))) return '—';
  if (typeof v !== 'number') return v;
  const sign = v < 0 ? '-' : '';
  return `${sign}₹${Math.abs(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const pctText = (v) => (v === null || v === undefined) ? '' : `${Number(v).toFixed(1)}%`;

const variance = (a, b) => {
  if (a === null || a === undefined || b === null || b === undefined) return { abs: null, pct: null };
  const abs = Number(b) - Number(a);
  const pct = a === 0 ? null : (abs / Number(a)) * 100;
  return { abs, pct };
};

function SectionHeader({ children }) {
  return (
    <tr className="bg-[#EDF1EE]">
      <td colSpan={6} className="px-3 py-1.5 text-[10.5px] uppercase tracking-wider font-semibold text-[color:var(--color-primary)]">
        {children}
      </td>
    </tr>
  );
}

function Row({ label, indent = 0, a, b, costPct, bold = false, color }) {
  const { abs, pct } = variance(a, b);
  const varColor = abs == null ? '#9CA3A0' : abs >= 0 ? 'var(--color-primary)' : '#A33A3A';
  const cls = bold ? 'font-semibold text-[color:var(--color-text-primary)]' : 'text-[color:var(--color-text-primary)]';
  return (
    <tr className="border-b border-[#F1F4F0] hover:bg-[#FAFBF8]"
        data-testid={`pnl-row-${label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}>
      <td className={`px-3 py-1.5 text-[12.5px] ${cls}`} style={{ paddingLeft: `${12 + indent * 14}px`, color }}>
        {label}
      </td>
      <td className={`px-3 py-1.5 text-right text-[12.5px] tabular-nums ${cls}`} style={{ color }}>{fmt(a)}</td>
      <td className={`px-3 py-1.5 text-right text-[12.5px] tabular-nums ${cls}`} style={{ color }}>{fmt(b)}</td>
      <td className="px-3 py-1.5 text-right text-[12.5px] tabular-nums" style={{ color: varColor }}>
        {abs == null ? '—' : (abs >= 0 ? '+' : '') + fmt(abs).replace('-', '')}
      </td>
      <td className="px-3 py-1.5 text-right text-[11px] tabular-nums" style={{ color: varColor }}>
        {pct == null ? '—' : (pct >= 0 ? '+' : '') + pct.toFixed(1) + '%'}
      </td>
      <td className="px-3 py-1.5 text-right text-[11px] text-[color:var(--color-text-muted)] tabular-nums">
        {costPct == null || costPct === undefined ? '' : pctText(costPct)}
      </td>
    </tr>
  );
}

/** Union of row keys present in either block's `_statement.<bucket>` array,
 *  preserving the order of first appearance. Lookup map for fast value access.
 *  Each row's display label comes from whichever block first defined it
 *  (both should have identical labels — guaranteed by the backend helper). */
function unionRows(a, b, bucket) {
  const arrA = a?._statement?.[bucket] || [];
  const arrB = b?._statement?.[bucket] || [];
  const seen = new Set();
  const order = [];
  const labels = {};
  const aMap = {};
  const bMap = {};
  for (const it of arrA) {
    if (!seen.has(it.key)) { seen.add(it.key); order.push(it.key); labels[it.key] = it.label; }
    aMap[it.key] = it;
  }
  for (const it of arrB) {
    if (!seen.has(it.key)) { seen.add(it.key); order.push(it.key); labels[it.key] = it.label; }
    bMap[it.key] = it;
  }
  return { order, labels, aMap, bMap };
}

export default function PnLStatementBody({ blockA, blockB, labelA = 'A', labelB = 'B' }) {
  const A = blockA || {};
  const B = blockB || {};
  const showRooms = A.rooms_enabled || B.rooms_enabled;

  // Derived totals (B side drives the Cost % column to keep it consistent
  // with MTD where the COST % shown is for the "current" period).
  //
  // Reconciled with the P&L statement engine (see backend
  // services/period_sources.py::_reconcile_statement_totals).
  //   • Total Operating Expenses := Σ op_ex_items[].actual   (backend
  //     ships this as `total_operating_expenses`; `variable_costs`
  //     retained as fallback for older shapes and mistakenly includes
  //     COGS on legacy blocks).
  //   • Total Fixed Costs        := Σ fixed_items[].actual   (backend
  //     ships `prorated_fixed` — used to be hard-set to 0, now
  //     reconciled).
  //   • Total Expenditure        := COGS + Op-Ex + Fixed
  //   • Net Profit               := Total Revenue − Total Expenditure
  const totalCogsA = (A.food_cost || 0) + (A.beverage_cost || 0);
  const totalCogsB = (B.food_cost || 0) + (B.beverage_cost || 0);
  const totalOpExA = (A.total_operating_expenses != null)
                      ? A.total_operating_expenses : (A.variable_costs || 0);
  const totalOpExB = (B.total_operating_expenses != null)
                      ? B.total_operating_expenses : (B.variable_costs || 0);
  const totalFixedA = A.prorated_fixed || 0;
  const totalFixedB = B.prorated_fixed || 0;
  const totalExpA = totalCogsA + totalOpExA + totalFixedA;
  const totalExpB = totalCogsB + totalOpExB + totalFixedB;
  const netProfitA = (A.total_revenue || 0) - totalExpA;
  const netProfitB = (B.total_revenue || 0) - totalExpB;
  const totalCostPct = B.total_revenue ? totalExpB / B.total_revenue * 100 : null;
  const netMarginPct = B.total_revenue ? netProfitB / B.total_revenue * 100 : null;

  const opEx = unionRows(A, B, 'op_ex_items');
  const fixed = unionRows(A, B, 'fixed_items');

  // Helper to read row value from a side. The backend stamps the row's
  // displayable value into `actual` — for actuals blocks it's the actual
  // value, for the budget view block it's the budget value (swapped at
  // _budget_baseline time). Renderer remains source-agnostic.
  const valOf = (map, key) => {
    const it = map[key];
    if (!it) return null;
    return (it.actual === undefined || it.actual === null) ? null : Number(it.actual);
  };

  return (
    <div className="bg-white border border-[#E2E8DF] rounded-lg overflow-hidden">
      <table className="w-full" data-testid="pnl-statement-body">
        <thead className="sticky top-0 z-10 shadow-[0_1px_0_0_rgba(0,0,0,0.06)]">
          <tr className="bg-[#F9F9F8] border-b border-[#E2E8DF]">
            <th className="px-3 py-2 text-left text-[10.5px] uppercase tracking-wider text-[color:var(--color-text-muted)] font-semibold bg-[#F9F9F8]">Line Item</th>
            <th className="px-3 py-2 text-right text-[10.5px] uppercase tracking-wider text-[color:var(--color-text-muted)] font-semibold bg-[#F9F9F8]">{labelA}</th>
            <th className="px-3 py-2 text-right text-[10.5px] uppercase tracking-wider text-[color:var(--color-text-muted)] font-semibold bg-[#F9F9F8]">{labelB}</th>
            <th className="px-3 py-2 text-right text-[10.5px] uppercase tracking-wider text-[color:var(--color-text-muted)] font-semibold bg-[#F9F9F8]">Variance</th>
            <th className="px-3 py-2 text-right text-[10.5px] uppercase tracking-wider text-[color:var(--color-text-muted)] font-semibold bg-[#F9F9F8]">Var %</th>
            <th className="px-3 py-2 text-right text-[10.5px] uppercase tracking-wider text-[color:var(--color-text-muted)] font-semibold bg-[#F9F9F8]">Cost %</th>
          </tr>
        </thead>
        <tbody>
          {/* ── REVENUE ── */}
          <SectionHeader>Revenue</SectionHeader>
          <Row label="Food Net Revenue"     indent={1} a={A.food_sale}     b={B.food_sale} />
          <Row label="Beverage Net Revenue" indent={1} a={A.beverage_sale} b={B.beverage_sale} />
          {showRooms && (
            <Row label="Room Revenue"       indent={1} a={A.room_revenue}  b={B.room_revenue} />
          )}
          <Row label="Total Revenue" a={A.total_revenue} b={B.total_revenue} bold />

          {/* ── COST OF GOODS SOLD ── */}
          <SectionHeader>Cost of Goods Sold</SectionHeader>
          <Row label="Food Cost"     indent={1} a={A.food_cost}     b={B.food_cost}     costPct={B.food_cost_pct} />
          <Row label="Beverage Cost" indent={1} a={A.beverage_cost} b={B.beverage_cost} costPct={B.beverage_cost_pct} />
          <Row label="Total COGS" a={totalCogsA} b={totalCogsB} bold
               costPct={B.total_revenue ? totalCogsB / B.total_revenue * 100 : null} />

          {/* ── GROSS PROFIT ── */}
          <Row label="Gross Profit" a={A.gross_profit} b={B.gross_profit} bold color="var(--color-primary)"
               costPct={B.gross_margin_pct} />

          {/* ── OPERATING EXPENSES (dynamic — driven by backend _statement) ── */}
          {opEx.order.length > 0 && (
            <>
              <SectionHeader>Operating Expenses</SectionHeader>
              {opEx.order.map((k) => {
                const av = valOf(opEx.aMap, k);
                const bv = valOf(opEx.bMap, k);
                return (
                  <Row key={`op-${k}`} label={opEx.labels[k]} indent={1}
                       a={av} b={bv}
                       costPct={B.total_revenue && bv != null ? (bv / B.total_revenue * 100) : null} />
                );
              })}
              <Row label="Total Operating Expenses" a={totalOpExA} b={totalOpExB} bold
                   costPct={B.total_revenue ? (totalOpExB / B.total_revenue * 100) : null} />
            </>
          )}

          {/* ── FIXED COSTS (dynamic — driven by backend _statement) ── */}
          {fixed.order.length > 0 && (
            <>
              <SectionHeader>Fixed Costs</SectionHeader>
              {fixed.order.map((k) => {
                const av = valOf(fixed.aMap, k);
                const bv = valOf(fixed.bMap, k);
                return (
                  <Row key={`fx-${k}`} label={fixed.labels[k]} indent={1}
                       a={av} b={bv}
                       costPct={B.total_revenue && bv != null ? (bv / B.total_revenue * 100) : null} />
                );
              })}
              <Row label="Total Fixed Costs" a={totalFixedA} b={totalFixedB} bold
                   costPct={B.total_revenue ? (totalFixedB / B.total_revenue * 100) : null} />
            </>
          )}

          {/* ── TOTAL EXPENDITURE ── */}
          <Row label="Total Expenditure" a={totalExpA} b={totalExpB} bold costPct={totalCostPct} />

          {/* ── NET PROFIT ── */}
          <Row label="Net Profit / (Loss)" a={netProfitA} b={netProfitB} bold
               color={netProfitB < 0 ? '#A33A3A' : 'var(--color-primary)'}
               costPct={netMarginPct} />
        </tbody>
      </table>
    </div>
  );
}

export { fmt };
